import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Check, ArrowRight, Anchor, Truck } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';
import SEO, { createServiceSchema } from '../components/SEO';

const skiBoatServices = [
  "Complete hull resprays",
  "Gel coat repairs",
  "Custom graphics and decals",
  "Anti-fouling application",
  "Fibreglass repairs"
];

const rvServices = [
  "Full exterior resprays",
  "Panel repairs and replacement",
  "Custom colour schemes",
  "Decal removal and replacement",
  "Clear coat protection"
];

const SkiBoatRVPage = () => {
  const { isDark } = useTheme();

  const serviceSchema = createServiceSchema(
    'Ski Boat & RV Resprays',
    'Professional boat and RV respray services including hull resprays, gel coat repairs, caravan resprays, and custom graphics in Murray Bridge, South Australia.',
    'https://www.jprcustoms.com.au/ski-boat-rv'
  );

  return (
    <div className={`min-h-screen ${isDark ? 'bg-neutral-950' : 'bg-white'}`}>
      <SEO 
        title="Ski Boat & RV Resprays"
        description="Professional boat and RV respray services in Murray Bridge SA. Hull resprays, gel coat repairs, fibreglass repairs, caravan resprays, custom graphics. Call +61 417 119 544."
        keywords="boat respray, ski boat painting, RV respray, caravan respray, gel coat repairs, fibreglass repairs, boat paint Murray Bridge"
        canonical="/ski-boat-rv"
        structuredData={serviceSchema}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=1920&q=80)` }}
          >
            <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-neutral-950/95 via-neutral-950/80 to-neutral-950/60' : 'bg-gradient-to-r from-white/95 via-white/85 to-white/70'}`} />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 pt-44">
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${isDark ? 'text-white' : 'text-neutral-900'}`}>
              Ski Boat &<br />
              <span className="text-lime-500">RV Resprays</span>
            </h1>
            <p className={`text-xl max-w-2xl mb-8 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
              Professional respray services for boats, caravans, and recreational vehicles.
            </p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 rounded-lg"
            >
              <Phone className="w-5 h-5" />
              Get a Free Quote
            </a>
          </div>
        </section>

        {/* Content */}
        <section className={`py-20 ${isDark ? 'bg-neutral-900' : 'bg-neutral-50'}`}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              {/* Ski Boat Services */}
              <div className={`p-8 rounded-xl ${isDark ? 'bg-neutral-800' : 'bg-white shadow-lg'}`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-lime-500 rounded-full flex items-center justify-center">
                    <Anchor className="w-7 h-7 text-black" />
                  </div>
                  <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-neutral-900'}`}>Ski Boat Services</h2>
                </div>
                <p className={`mb-6 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
                  Give your boat a fresh new look with our professional respray services.
                </p>
                <ul className="space-y-3">
                  {skiBoatServices.map((service, index) => (
                    <li key={index} className={`flex items-center gap-3 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                      <Check className="w-5 h-5 text-lime-500 flex-shrink-0" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* RV Services */}
              <div className={`p-8 rounded-xl ${isDark ? 'bg-neutral-800' : 'bg-white shadow-lg'}`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-lime-500 rounded-full flex items-center justify-center">
                    <Truck className="w-7 h-7 text-black" />
                  </div>
                  <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-neutral-900'}`}>RV & Caravan Services</h2>
                </div>
                <p className={`mb-6 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
                  Refresh your RV or caravan with a professional respray.
                </p>
                <ul className="space-y-3">
                  {rvServices.map((service, index) => (
                    <li key={index} className={`flex items-center gap-3 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                      <Check className="w-5 h-5 text-lime-500 flex-shrink-0" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <img 
                src="https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&q=80" 
                alt="Boat services"
                className="w-full h-64 object-cover rounded-xl"
              />
              <img 
                src="https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?w=800&q=80" 
                alt="RV services"
                className="w-full h-64 object-cover rounded-xl"
              />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={`py-16 ${isDark ? 'bg-neutral-800' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Ready to Refresh Your Boat or RV?</h2>
            <p className={`mb-8 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>Contact us for a free assessment and quote.</p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 rounded-lg"
            >
              Call {companyInfo.phone}
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default SkiBoatRVPage;
